// API Response Types
export interface ApiResponse<T> {
  data: T
  message?: string
  status: number
}

export interface PaginatedResponse<T> {
  data: T[]
  total: number
  page: number
  limit: number
}

// Common Status Enum
export enum Status {
  ACTIVE = 'active',
  INACTIVE = 'inactive'
}

// Inverter Type Enum
export enum InverterType {
  INVERTER = 'INVERTER',
  MICROINVERTER = 'MICROINVERTER'
}

// New/Existing Enum
export enum NewExistingType {
  NEW = 'N',
  EXISTING = 'E'
}

// Module Brand Types
export interface ModuleBrand {
  id: string
  name: string
  createdAt: string
  updatedAt: string
}

export interface SolarModule {
  id: string
  fullName: string
  modelNumber: string
  wattage: number
  width?: number
  height?: number
  status: Status
  brands: ModuleBrand[]
  createdAt: string
  updatedAt: string
}

// Query Parameters
export interface SolarModuleQuery {
  brandId?: string
}

// Inverter Brand Types
export interface InverterBrand {
  id: string
  name: string
  inverter_type: InverterType
  created_at: string
  updated_at: string
}

export interface Inverter {
  id: string
  model: string
  status: Status
  wattage?: number
  current?: number
  manufacturer: InverterBrand
  manufacturer_id: string
  created_at: string
  updated_at: string
}

export interface InverterQuery {
  brandId?: string
}

export interface Optimizer {
  id: string
  type: string
  current?: number
  status: Status
  created_at: string
  updated_at: string
}

// Combiner Types
export interface Combiner {
  id: string
  name: string
  inverter_brand_id: string
  inverter_brand: InverterBrand
  created_at: string
  updated_at: string
}

// RSD Types
export interface Rsd {
  id: string
  name: string
  inverter_brand_id: string
  inverter_brand: InverterBrand
  created_at: string
  updated_at: string
}

// Battery Brand Types
export interface BatteryBrand {
  id: string
  name: string
  created_at: string
  updated_at: string
}

export interface Battery {
  id: string
  batteryName: string
  batteryModel: string
  kwh?: number
  current?: number
  wattage?: number
  status: Status
  batteryBrand: BatteryBrand
  batteryBrandId: string
  created_at: string
  updated_at: string
}

// Structural Types
export enum StructuralCategory {
  FRAMING_SIZE = 'framing_size',
  FRAMING_TYPE = 'framing_type',
  RAFTER_SPACING = 'rafter_spacing',
  MOUNT_SYSTEM = 'mount_system'
}

export interface Structural {
  id: string
  category: StructuralCategory
  value: string
  displayOrder: number
  status: Status
  created_at: string
  updated_at: string
}

// MSP Types
export enum MspCategory {
  MSP_BUSS = 'msp_buss',
  MSP_BREAKER = 'msp_breaker',
  MSP_MANUFACTURER = 'msp_manufacturer',
  MSP_LOCATION = 'msp_location',
  MSP_STYLE = 'msp_style',
  MSP_TYPE = 'msp_type',
  MSP_CAD_LOCATION = 'msp_cad_location'
}

export interface Msp {
  id: string
  category: MspCategory
  value: string
  displayOrder: number
  status: Status
  created_at: string
  updated_at: string
}

// POI Types
export interface Poi {
  id: string
  model: string
  created_at: string
  updated_at: string
  locations?: PoiLocation[]
}

export interface PoiLocation {
  id: string
  poi_id: string
  location_name: string
  is_active: boolean
  created_at: string
  updated_at: string
  poi?: Poi
}

export interface AtsBreakerSize {
  id: string
  amperage: number
  is_active: boolean
  created_at: string
  updated_at: string
}

// DISCO Types
export enum DiscoType {
  FUSED = 'FUSED',
  NON_FUSED = 'NON-FUSED'
}

export interface Disco {
  id: string
  modelNumber: string
  brand: string
  discoRating: number
  type: DiscoType
  fuseRatings: number[]
  created_at: string
  updated_at: string
}

// CONDUIT Types
export enum ConduitCategory {
  SIZE = 'size',
  TYPE = 'type'
}

export interface Conduit {
  id: string
  category: ConduitCategory
  value: string
  created_at: string
  updated_at: string
}

// RAIL Types
export interface Rail {
  id: string
  name: string
  length?: number
  status: Status
  domestic_status: DomesticStatus
  attachments?: Attachment[]
  createdAt: string
  updatedAt: string
}

// ATTACHMENT Types
export enum DomesticStatus {
  DOM = 'DOM',
  NON_DOM = 'NON_DOM'
}

export enum PanelOrientation {
  LANDSCAPE = 'LANDSCAPE',
  PORTRAIT = 'PORTRAIT'
}

export interface Attachment {
  id: string
  name: string
  status: Status
  rails: Rail[]
  createdAt: string
  updatedAt: string
}

// Construction Review Types
export interface ConstructionReview {
  id: string
  task_id: string
  task_name: string
  generated_cr: Record<string, any>
  updated_cr: Record<string, any>
  changes: Record<string, any>
  structured_data: Record<string, any> | null
  aurora_full_data: Record<string, any> | null
  created_by: { id: string; email: string; name: string; avatar_url: string } | null
  updated_by: { id: string; email: string; name: string; avatar_url: string } | null
  created_at: string
  updated_at: string
}

// Building Attribute Types
export interface RoofType {
  id: string
  type: string
  is_active: Status
  created_at: string
  updated_at: string
}

export interface RoofCondition {
  id: string
  value: string
  created_at: string
  updated_at: string
}

export interface ReroofStatus {
  id: string
  value: string
  created_at: string
  updated_at: string
}

export interface BuildingHeight {
  id: string
  value: string
  created_at: string
  updated_at: string
}

export interface ElectricConfig {
  id: string
  type: string
  value: string
  created_at: string
  updated_at: string
}

export interface DiscoDetails {
  id: string;
  type: 'text' | 'location';
  value: string;
  created_at?: string;
  updated_at?: string;
}

export interface Setting {
  id: string;
  type: string;
  value: unknown;
  description?: string;
  created_at: string;
  updated_at: string;
}
